// @givmo/social — connecting an account: the OAuth leg, start to finish (SMM-WEB1; §SMM-8 point 2;
// the store's contract `org-integrations.md` §4, "the console finishes the OAuth leg").
//
// ══ THE SHAPE ═══════════════════════════════════════════════════════════════════════════════════
//
//   1. START. `POST /org/{org_ref}/integrations/oauth/start` for the family's first offered provider,
//      `purpose=social_publishing`, `account_kind=org_account`, the return page as `redirect_uri`
//      (`socialRedirectUri`: this console's origin + `/social/connected`, registered exactly with the
//      platforms and in the backend's allowlist) and the asking page as `return_path`.
//   2. A FULL-PAGE TRIP to the platform — never a popup or a frame (§SMM-8: the platforms send
//      frame-blocking headers, and one window is one less thing to lose). The tab keeps, in its own
//      `sessionStorage`, which family it went to and where it came from, so the return page can name
//      the platform in its words and send the person back.
//   3. THE RETURN (`connect-return-page.tsx`) posts `{state, code, redirect_uri}` to
//      `oauth/complete-accounts` ONCE, shows the account picker or the success, refreshes the
//      accounts, and clears the parameters from the address.

import { firstErrorCode, isGivmoError, sdk } from '@givmo/api-client';
import { redirectTo } from './social-navigation';
import { grantFamilyLabel, startingProvider } from './social-platforms';
import { SOCIAL_PURPOSE, SOCIAL_ROUTES, socialRedirectUri } from './social-routes';
import { connectRefusalWords } from './social-words';

/** This tab's record of the connect it is on (written at the start, read at the return). */
export const CONNECT_TRIP_KEY = 'givmo.social.connect-trip';
/** This tab's pending choice, so a reload of the return page can still finish it. */
export const PENDING_GRANT_KEY = 'givmo.social.pending-grant';

export interface ConnectTrip {
  orgRef: string;
  /** The family's words ("Facebook & Instagram", "LinkedIn"). */
  familyLabel: string;
  /** What the connect started from, so the return page can start it again ("Try again"). */
  start?: ConnectStart | null;
  /** Where the person asked from, where they go back to. */
  returnPath: string;
  /** A reconnect names the account it reconnects. */
  reconnect: boolean;
}

function store(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

export function keepTrip(trip: ConnectTrip): void {
  try {
    store()?.setItem(CONNECT_TRIP_KEY, JSON.stringify(trip));
  } catch {
    // Storage refused: the return page still finishes the connect, in plainer words.
  }
}

/** The trip this tab is on, for one organization; null when there is none (or it's another's). */
export function readTrip(orgRef: string): ConnectTrip | null {
  try {
    const raw = store()?.getItem(CONNECT_TRIP_KEY) ?? null;
    if (raw === null) return null;
    const value = JSON.parse(raw) as Record<string, unknown>;
    if (value.orgRef !== orgRef) return null;
    return {
      orgRef,
      familyLabel: typeof value.familyLabel === 'string' ? value.familyLabel : 'the platform',
      returnPath:
        typeof value.returnPath === 'string' && value.returnPath.startsWith('/')
          ? value.returnPath
          : SOCIAL_ROUTES.accounts,
      reconnect: value.reconnect === true,
      start: readStart(value.start),
    };
  } catch {
    return null;
  }
}

function readStart(value: unknown): ConnectStart | null {
  if (value === null || typeof value !== 'object') return null;
  const start = value as Record<string, unknown>;
  if (
    start.kind === 'connect' &&
    typeof start.family === 'string' &&
    Array.isArray(start.providers)
  ) {
    const providers = start.providers.filter((entry): entry is string => typeof entry === 'string');
    return { kind: 'connect', family: start.family, providers };
  }
  if (
    start.kind === 'reconnect' &&
    typeof start.connectionId === 'string' &&
    typeof start.provider === 'string' &&
    typeof start.family === 'string'
  ) {
    return {
      kind: 'reconnect',
      connectionId: start.connectionId,
      provider: start.provider,
      family: start.family,
    };
  }
  return null;
}

export function forgetTrip(): void {
  try {
    store()?.removeItem(CONNECT_TRIP_KEY);
    store()?.removeItem(PENDING_GRANT_KEY);
  } catch {
    // Nothing to forget.
  }
}

export function keepPendingGrant(orgRef: string, grantId: string): void {
  try {
    store()?.setItem(PENDING_GRANT_KEY, JSON.stringify({ orgRef, grantId }));
  } catch {
    // A reload then starts again from Accounts.
  }
}

export function readPendingGrant(orgRef: string): string | null {
  try {
    const raw = store()?.getItem(PENDING_GRANT_KEY) ?? null;
    if (raw === null) return null;
    const value = JSON.parse(raw) as Record<string, unknown>;
    return value.orgRef === orgRef && typeof value.grantId === 'string' ? value.grantId : null;
  } catch {
    return null;
  }
}

/** What a connect starts from: a family's Connect, or one account's Reconnect. */
export type ConnectStart =
  | { kind: 'connect'; family: string; providers: readonly string[] }
  | { kind: 'reconnect'; connectionId: string; provider: string; family: string };

/** The asking page, as the address bar has it (a query's slashes kept plain: the store refuses an
 *  encoded slash in a return path, as Board's connect learned). */
function currentPath(): string {
  const location = globalThis.location;
  if (location === undefined) return SOCIAL_ROUTES.accounts;
  return `${location.pathname}${location.search.replace(/%2F/gi, '/')}`;
}

/**
 * Start a connect and send the tab to the platform. Resolves only on a refusal, with its words
 * (the trip itself never resolves: the page leaves).
 */
export async function startConnect(
  orgRef: string,
  start: ConnectStart,
  go: (url: string) => void = redirectTo,
  /** Where the person comes back to, when not the page they are on (a draft just saved). */
  returnTo: string | null = null,
): Promise<string> {
  const providers =
    start.kind === 'connect' ? start.providers : ([start.provider] as readonly string[]);
  const familyLabel = grantFamilyLabel(start.family, providers);
  const provider =
    start.kind === 'reconnect' ? start.provider : startingProvider(start.family, providers);
  if (provider === null) return connectRefusalWords(null, familyLabel);
  const returnPath = returnTo ?? currentPath();
  try {
    const { data } = await sdk.orgIntegrationsStartOrgIntegrationOauth({
      path: { org_ref: orgRef },
      body: {
        provider,
        purpose: SOCIAL_PURPOSE,
        account_kind: 'org_account',
        redirect_uri: socialRedirectUri(),
        return_path: returnPath,
        ...(start.kind === 'reconnect' ? { reconnect_connection_id: start.connectionId } : {}),
      },
      throwOnError: true,
    });
    keepTrip({ orgRef, familyLabel, returnPath, reconnect: start.kind === 'reconnect', start });
    go(data.authorization_url);
    return '';
  } catch (error) {
    const code = isGivmoError(error) ? firstErrorCode(error) : null;
    return connectRefusalWords(code, familyLabel);
  }
}
